/* Employee Sales Portal — Dashboard + Leads (create, duplicate detection,
   tracked welcome email, copy link, timeline, activity). */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { PortalTopBar } from "@/components/PortalTopBar";
import { PhoneField, formatPhoneDisplay } from "@/components/PhoneField";
import SandboxPanel from "@/components/SandboxPanel";
import { WelcomeSection } from "@/components/WelcomeSection";
import {
  LayoutDashboard, Users, Loader2, Plus, Mail, Copy, Clock, Target,
  ArrowLeft, Building2, School, AlertTriangle, CheckCircle2, X,
  FlaskConical, Trash2, Sparkles, Play, UserCog, Settings2, ExternalLink, DollarSign, Share2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const sget = (p) => axios.get(`${API}${p}`, cfg).then((r) => r.data);
const spost = (p, b) => axios.post(`${API}${p}`, b || {}, cfg).then((r) => r.data);

const STAGE_COLORS = {
  New: "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
  Contacted: "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
  "Account Created": "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
  "Expired Claim": "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
  "Not Interested": "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
  "Closed Lost": "bg-white text-[#334155] border border-[#94a3b8]",
  Engaged: "bg-[#e6f4f8] text-[#00698a] border border-[#bfe3ee]",
  Activating: "bg-[#e6f4f8] text-[#00698a] border border-[#bfe3ee]",
  "Active Customer": "bg-[#e6f4f8] text-[#00698a] border border-[#bfe3ee]",
  "Closed Won": "bg-[#00698a] text-white",
  Customer: "bg-[#00698a] text-white",
  "Renewal/Closed": "bg-white text-[#00698a] border border-[#007a9e]",
  "Referral Sent": "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
  Visited: "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
  Registered: "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
  "Pilot Active": "bg-[#e6f4f8] text-[#00698a] border border-[#bfe3ee]",
};
const STAGE_FALLBACK = "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const claimExp = (d) => {
  if (!d) return "—";
  const days = Math.ceil((new Date(d) - new Date()) / 86400000);
  if (days >= 0 && days <= 35) {
    return <span className="font-semibold text-slate-700">{fmtDate(d)} <span className="font-normal text-slate-400">· in {days} day{days === 1 ? "" : "s"}</span></span>;
  }
  return fmtDate(d);
};

// S3.1 — display-only stage label (never written back to the stored value)
const STAGE_LEGACY = {
  new_lead: "New", new: "New", contacted: "Contacted", discovery: "Engaged",
  demonstration: "Engaged", proposal: "Engaged", po_pending: "Activating",
  won: "Closed Won", closed_won: "Closed Won", lost: "Closed Lost",
  closed_lost: "Closed Lost", renewal: "Renewal/Closed",
};
const stageLabel = (s) =>
  STAGE_COLORS[s] ? s : (STAGE_LEGACY[s] || String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));

// Stage chip with neutral x icon for Closed Lost (S3.2)
const StageChip = ({ stage, className = "" }) => {
  const label = stageLabel(stage);
  return (
    <Badge className={`text-[11px] ${STAGE_COLORS[label] || STAGE_FALLBACK} ${className}`}>
      {label === "Closed Lost" && <X className="h-3 w-3 mr-0.5" />}
      {label}
    </Badge>
  );
};

const TestBadge = () => {
  const demo = typeof window !== "undefined" && window.sessionStorage?.impersonation_mode === "demo";
  return (
    <span data-testid="test-data-badge" className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1] align-middle">{demo ? "Demo data" : "Test Data"}</span>
  );
};

const PERIODS = [
  ["this_week", "This Week"], ["last_week", "Last Week"], ["this_month", "This Month"],
  ["last_month", "Last Month"], ["this_quarter", "This Quarter"], ["last_quarter", "Last Quarter"],
  ["this_year", "This Year"], ["last_year", "Last Year"], ["custom", "Custom Range"], ["all", "All Time"],
];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const PeriodFilter = ({ period, setPeriod, start, end, setStart, setEnd }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="period-filter">
    <select value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 rounded-md border border-slate-200 text-sm px-2 bg-white" data-testid="period-select">
      {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    {period === "custom" && (
      <>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 rounded-md border border-slate-200 text-sm px-2" data-testid="period-start" />
        <span className="text-slate-400 text-sm">to</span>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 rounded-md border border-slate-200 text-sm px-2" data-testid="period-end" />
      </>
    )}
  </div>
);

const useCommission = () => {
  const [period, setPeriod] = useState("this_month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const qs = () => {
    let q = `period=${period}`;
    if (period === "custom" && start && end) q += `&start=${start}&end=${end}`;
    return q;
  };
  useEffect(() => {
    if (period === "custom" && (!start || !end)) return;
    setLoading(true);
    sget(`/api/sales/commission?${qs()}`)
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => { setData(null); setErr({ status: e?.response?.status || 0, detail: e?.response?.data?.detail || "" }); })
      .finally(() => setLoading(false));
  }, [period, start, end, nonce]);
  const retry = () => setNonce((n) => n + 1);
  return { period, setPeriod, start, setStart, end, setEnd, data, err, loading, retry, qs };
};

/* ── Dashboard ── */
const Dashboard = ({ me, onGoLeads, onNewLead, onGoCommission }) => {
  const [data, setData] = useState(null);
  const c = useCommission();
  useEffect(() => { sget("/api/sales/me").then(setData).catch(() => {}); }, []);
  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  const k = data.kpis;
  const repName = (me?.name || me?.full_name || data?.name || me?.email || "").split(/[ @]/)[0];
  const welcomeItems = [];
  if (k.new_leads > 0) welcomeItems.push({ signature: "sales:new_leads", priority: 2,
    lead: `You have ${k.new_leads} new lead${k.new_leads === 1 ? "" : "s"} waiting to be opened.`,
    actions: [{ label: "Open leads", primary: true, onClick: () => onGoLeads && onGoLeads() }] });
  if (k.renewal_pending > 0) welcomeItems.push({ signature: "sales:renewals", priority: 3,
    lead: `${k.renewal_pending} renewal${k.renewal_pending === 1 ? "" : "s"} pending follow-up.`,
    actions: [{ label: "Review renewals", primary: true, onClick: () => onGoLeads && onGoLeads() }] });
  const cm = c.data;
  const commErr = !cm && c.err;
  const errHelp = c.err && c.err.status === 400 ? "Commission isn't set up yet" : "Couldn't load";
  const tileVal = (v) => (commErr ? "Unavailable" : (c.loading && !cm ? "—" : v));
  const periodLabel = ({ this_month: "this month", last_month: "last month", this_quarter: "this quarter", last_quarter: "last quarter", this_year: "this year", custom: "custom range" })[c.period] || "this month";
  const commTiles = [
    { label: "Sales Commission", val: tileVal(cm ? `~${money(cm.earned_commission)}` : "—"), help: "Approx. before deductions & taxes.", accent: "text-[#00698a]", testid: "tile-sales-commission" },
    { label: "Pipeline Commission", val: tileVal(cm ? `~${money(cm.pipeline_commission)}` : "—"), help: "Potential only — not earned until won & paid.", accent: "text-slate-900", testid: "tile-pipeline-commission" },
    { label: "Revenue Received", val: tileVal(cm ? money(cm.revenue_received) : "—"), help: "Collected revenue this period.", accent: "text-slate-900", testid: "tile-revenue-received" },
    { label: "Renewals", val: tileVal(cm ? cm.renewals_count : "—"), help: cm ? `${money(cm.renewals_amount)} due` : "Upcoming renewals.", accent: "text-slate-900", testid: "tile-renewals" },
  ];
  const cards = [
    ["Total Leads", k.total_leads], ["New", k.new_leads], ["Welcome Sent", k.welcome_sent],
    ["Link Clicked", k.link_clicked], ["Signup Started", k.signup_started], ["Accounts Created", k.accounts_created],
    ["Active Customers", k.active_customers], ["Renewal Pending", k.renewal_pending],
  ];
  const convClosed = (k.closed_won_count || 0) + (k.closed_lost_count || 0);
  const finCards = [
    { label: "Pipeline $", val: money(k.pipeline_value), sub: `${k.pipeline_count} open · ${k.schools_in_pipeline} schools`, accent: "text-slate-900", testid: "fin-pipeline-value" },
    { label: "Closed / Won $", val: money(k.closed_won_value), sub: `${k.closed_won_count} won · ${k.schools_sold} schools`, accent: "text-slate-900", testid: "fin-closed-won" },
    { label: "Avg Deal Size", val: money(k.avg_deal_size), sub: "Actual contract value", accent: "text-slate-900", testid: "fin-avg-deal" },
    { label: "Conversion Rate",
      val: (k.conversion_rate == null || convClosed === 0) ? "—" : `${k.conversion_rate}%`,
      sub: (k.conversion_rate == null || convClosed === 0) ? "No closed deals yet" : `${k.closed_won_count} won · ${k.closed_lost_count} lost · open leads excluded`,
      accent: "text-slate-900", testid: "fin-conversion" },
  ];
  return (
    <div data-testid="sales-dashboard">
      <WelcomeSection name={repName} items={welcomeItems} testid="sales-welcome" />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Your pipeline and commission at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodFilter {...c} />
          <Button onClick={() => onNewLead && onNewLead()} className="h-9 bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="dashboard-new-lead-btn"><Plus className="h-4 w-4 mr-1" />New Lead</Button>
        </div>
      </div>
      <div className="mt-5">
        <SandboxPanel authFetch={(url, opts = {}) => fetch(url, { ...opts, credentials: "include" })} onChanged={() => sget("/api/sales/me").then(setData).catch(() => {})} />
      </div>
      <div className="mt-6 flex items-end justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-800" data-testid="dashboard-commission-heading">Commission · {periodLabel}</h2>
        <span className="text-[11px] text-slate-400 text-right">Approximate — before deductions, taxes and admin adjustments.</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
        {commTiles.map((t) => (
          <div key={t.label} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col" data-testid={t.testid}>
            <div className={`font-bold ${commErr ? "text-[16px] text-slate-500" : `text-2xl ${t.accent}`}`}>{t.val}</div>
            <div className="text-[12px] font-semibold text-slate-700 mt-0.5">{t.label}</div>
            {commErr ? (
              <div className="text-[10px] text-slate-400 mt-1 leading-tight">{errHelp} · <button type="button" onClick={() => c.retry()} className="font-semibold text-[#00698a] hover:underline" data-testid="tile-commission-retry">Retry</button></div>
            ) : (
              <div className="text-[10px] text-slate-400 mt-1 leading-tight">{t.help}</div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 flex items-end justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-800" data-testid="dashboard-pipeline-heading">Pipeline · all time</h2>
        <span className="text-[11px] text-slate-400 text-right">Not affected by the period filter.</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2" data-testid="sales-financial-kpis">
        {finCards.map((t) => (
          <div key={t.label} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col" data-testid={t.testid}>
            <div className={`text-2xl font-bold ${t.accent}`}>{t.val}</div>
            <div className="text-[12px] font-semibold text-slate-700 mt-0.5">{t.label}</div>
            <div className="text-[10px] text-slate-400 mt-1 leading-tight">{t.sub}</div>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <h2 className="text-base font-semibold text-slate-800" data-testid="dashboard-funnel-heading">Lead funnel</h2>
        <p className="text-[11px] text-slate-400">Where each of your {k.total_leads} leads is today</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
        {cards.map(([label, val], ci) => (
          <div key={label} className={`rounded-xl border border-slate-200 p-4 ${ci === 0 ? "bg-slate-50" : "bg-white"}`} data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="text-2xl font-bold text-slate-900">{val}</div>
            <div className="text-[12px] text-slate-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3"><Target className="h-4 w-4 text-[#007a9e]" /><h2 className="font-semibold text-slate-800">Next Best Actions</h2></div>
        {data.next_best_actions.length === 0 ? (
          <p className="text-sm text-slate-500">You're all caught up. Create a lead to get started.</p>
        ) : (
          <ul className="space-y-2" data-testid="next-best-actions">
            {data.next_best_actions.map((n, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                <span className="h-6 w-6 rounded-full bg-[#e6f4f8] text-[#00698a] grid place-items-center text-[11px] font-bold">{n.count}</span>
                {n.text}
              </li>
            ))}
          </ul>
        )}
      </div>
      {cm && Array.isArray(cm.deals) && cm.deals.length > 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="dashboard-collected-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-800">Collected this month</h2>
            <button type="button" onClick={() => onGoCommission && onGoCommission()} className="text-[12px] font-semibold text-[#00698a] hover:underline" data-testid="dashboard-collected-link">Commission →</button>
          </div>
          <div className="divide-y divide-slate-100">
            {cm.deals.slice(0, 5).map((d, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm">
                <span className="font-medium text-slate-800 truncate">{d.organization_name || d.organization || d.account || "—"}</span>
                <span className="flex items-center gap-4 shrink-0">
                  <span className="font-semibold text-slate-900 tabular-nums">{money(d.revenue != null ? d.revenue : d.amount)}</span>
                  <span className="text-[12px] text-slate-400">{fmtDate(d.collected_date || d.date)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
const Commission = () => {
  const c = useCommission();
  const d = c.data;
  const exportCsv = () => {
    let q = `period=${c.period}`;
    if (c.period === "custom" && c.start && c.end) q += `&start=${c.start}&end=${c.end}`;
    window.open(`${API}/api/sales/commission/export?${q}`, "_blank");
  };
  return (
    <div data-testid="sales-commission">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Commission</h1>
          <p className="text-sm text-slate-500 mt-1">Approximate — before deductions, taxes, and admin adjustments. Not final payroll.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PeriodFilter {...c} />
          <Button size="sm" variant="ghost" onClick={exportCsv} className="text-[#00698a] hover:bg-[#e6f4f8]" data-testid="commission-export-btn"><ExternalLink className="h-4 w-4 mr-1" />Export CSV</Button>
        </div>
      </div>
      {c.loading && !d ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5" data-testid="commission-loading" aria-busy="true">
          {[0, 1, 2, 3].map((i) => <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 h-20 animate-pulse" />)}
        </div>
      ) : c.err ? (
        (() => {
          const is400setup = c.err.status === 400 && (!c.err.detail || /commission/i.test(c.err.detail));
          const title = is400setup ? "Commission isn't set up for your account" : "We couldn't load your commission";
          const body = is400setup
            ? "An admin needs to turn on commission and set your rate in Admin → Employees → Commission. Your leads and pipeline are not affected."
            : (c.err.status === 400 ? c.err.detail : "This is usually temporary. Your data is safe.");
          return (
            <div role="alert" data-testid="commission-error" className="mt-5 rounded-xl border border-slate-200 bg-white p-8 text-center max-w-lg mx-auto">
              <AlertTriangle className="h-6 w-6 text-slate-400 mx-auto" />
              <h2 className="mt-3 font-bold text-slate-800">{title}</h2>
              <p className="mt-1 text-[13px] text-slate-500">{body}</p>
              <div className="mt-4 flex justify-center gap-2">
                <Button onClick={() => c.retry()} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="commission-retry">Retry</Button>
                {c.err.status === 400 && <Button variant="outline" onClick={() => document.querySelector("[data-testid='give-feedback-btn'],[data-testid='portal-feedback-btn'],[data-testid='feedback-trigger']")?.click()} data-testid="commission-feedback">Give feedback</Button>}
              </div>
            </div>
          );
        })()
      ) : (!d.rate_percent) ? (
        <div data-testid="commission-not-eligible" className="mt-5 rounded-xl border border-slate-200 bg-white p-8 text-center max-w-lg mx-auto">
          <h2 className="font-bold text-slate-800">You're not on a commission plan.</h2>
          <p className="mt-1 text-[13px] text-slate-500">Revenue you bring in is still tracked under Leads and My Licenses.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-2xl font-bold text-[#00698a]">~{money(d.earned_commission)}</div><div className="text-[12px] text-slate-600 mt-0.5">Approx. Earned Commission</div></div>
            <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-2xl font-bold text-slate-900">{money(d.revenue_received)}</div><div className="text-[12px] text-slate-600 mt-0.5">Revenue Received</div></div>
            <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-2xl font-bold text-[#00698a]">~{money(d.pipeline_commission)}</div><div className="text-[12px] text-slate-600 mt-0.5">Pipeline Commission (potential)</div></div>
            <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-2xl font-bold text-slate-900">{d.rate_percent}%</div><div className="text-[12px] text-slate-600 mt-0.5">Commission Rate</div></div>
          </div>
          <h2 className="mt-8 font-semibold text-slate-800">Collected Deals</h2>
          <div className="mt-2 rounded-xl border border-slate-200 bg-white sm:overflow-x-auto">
            {d.deals.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-sm text-slate-400">No collected revenue in this period.</div>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => c.setPeriod("this_quarter")} data-testid="commission-show-quarter">Show this quarter</Button>
              </div>
            ) : (
              <>
              <table className="hidden sm:table w-full text-sm min-w-[560px]">
                <thead><tr className="text-left text-[11.5px] uppercase text-slate-500 bg-[#f9fafb] border-b border-slate-100"><th className="p-3">Account</th><th className="p-3">Type</th><th className="p-3">Amount</th><th className="p-3">Payment Date</th><th className="p-3">Approx. Commission</th></tr></thead>
                <tbody>
                  {d.deals.map((x, i) => (
                    <tr key={i} className="border-b border-slate-50" data-testid="commission-deal-row">
                      <td className="p-3 text-slate-700">{x.account}{x.is_test_data && <TestBadge />}</td>
                      <td className="p-3"><Badge variant="outline" className="text-[11px]">{x.type}</Badge></td>
                      <td className="p-3 text-slate-700">{money(x.amount)}</td>
                      <td className="p-3 text-slate-500">{fmtDate(x.date)}</td>
                      <td className="p-3 font-semibold text-[#00698a]">~{money(x.commission)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="sm:hidden divide-y divide-slate-100">
                {d.deals.map((x, i) => (
                  <div key={i} className="p-4" data-testid="commission-deal-card">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-slate-800">{x.account}{x.is_test_data && <TestBadge />}</div>
                      <Badge variant="outline" className="text-[11px] shrink-0">{x.type}</Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-500">
                      <span>Amount: {money(x.amount)}</span>
                      <span>Paid: {fmtDate(x.date)}</span>
                      <span className="font-semibold text-[#00698a]">~{money(x.commission)}</span>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const LeadField = ({ label, k, type = "text", full, form, set, phone }) => (
  <div className={full ? "col-span-2" : ""}>
    <Label className="text-xs text-slate-600">{label}</Label>
    {phone ? (
      <PhoneField value={form[k]} onChange={(v) => set(k, v)} testid={`lead-${k}`} className="mt-1" />
    ) : (
      <Input value={form[k]} onChange={(e) => set(k, e.target.value)} type={type} className="mt-1 h-9 text-sm" data-testid={`lead-${k}`} />
    )}
  </div>
);

/* ── Create Lead modal (with duplicate detection) ── */
const CreateLeadModal = ({ onClose, onCreated }) => {
  const empty = { lead_type: "portfolio", organization_name: "", contact_first_name: "", contact_last_name: "",
    contact_title: "", contact_email: "", contact_phone: "", state: "", city: "", website: "", notes: "", lead_source: "Direct",
    expected_close_date: "", estimated_revenue: "", projected_schools: "" };
  const [form, setForm] = useState(empty);
  const [valuation, setValuation] = useState(null);
  const [showCalc, setShowCalc] = useState(false);
  const [dups, setDups] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const n = parseInt(form.projected_schools, 10);
    if (!n || n <= 0) { setValuation(null); return; }
    let alive = true;
    sget(`/api/sales/pipeline-valuation?schools=${n}&lead_type=${form.lead_type}`)
      .then((v) => alive && setValuation(v)).catch(() => alive && setValuation(null));
    return () => { alive = false; };
  }, [form.projected_schools, form.lead_type]);

  const check = async () => {
    setChecking(true);
    try {
      const r = await spost("/api/sales/leads/check-duplicate", {
        contact_email: form.contact_email, organization_name: form.organization_name,
        website: form.website, contact_phone: form.contact_phone });
      setDups(r);
    } catch { setDups({ has_duplicates: false, matches: [] }); }
    finally { setChecking(false); }
  };

  const save = async (override = false) => {
    if (!form.organization_name.trim()) return toast.error("Organization name is required");
    setSaving(true);
    try {
      const payload = { ...form, override_duplicate: override,
        estimated_revenue: form.estimated_revenue ? Number(form.estimated_revenue) : null,
        projected_schools: form.projected_schools ? parseInt(form.projected_schools, 10) : null,
        expected_close_date: form.expected_close_date || null };
      const r = await spost("/api/sales/leads", payload);
      toast.success("Lead created — claimed for 18 months");
      onCreated(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409) toast.error(d);
      else toast.error(d || "Could not create lead");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="create-lead-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white">
          <h2 className="font-bold text-slate-900">New Lead</h2>
          <button onClick={onClose} data-testid="lead-modal-close"><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-xs text-slate-600">Lead Type</Label>
            <div className="mt-1 flex gap-2">
              {[["portfolio", "Portfolio", Building2], ["school", "Single School", School], ["other", "Other", Users]].map(([v, l, Icon]) => (
                <button key={v} onClick={() => set("lead_type", v)} data-testid={`lead-type-${v}`}
                  className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-lg border text-xs font-semibold ${form.lead_type === v ? "border-[#007a9e] bg-[#e6f4f8] text-[#00698a]" : "border-slate-200 text-slate-600"}`}>
                  <Icon className="h-4 w-4" />{l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LeadField form={form} set={set} label="Organization Name *" k="organization_name" full />
            <LeadField form={form} set={set} label="Contact First Name" k="contact_first_name" />
            <LeadField form={form} set={set} label="Contact Last Name" k="contact_last_name" />
            <LeadField form={form} set={set} label="Title" k="contact_title" />
            <LeadField form={form} set={set} label="Email" k="contact_email" type="email" />
            <LeadField form={form} set={set} label="Phone" k="contact_phone" phone full />
            <LeadField form={form} set={set} label="State" k="state" />
            <LeadField form={form} set={set} label="City" k="city" />
            <LeadField form={form} set={set} label="Website" k="website" full />
            <LeadField form={form} set={set} label="Expected Close Date" k="expected_close_date" type="date" />
            <LeadField form={form} set={set} label="Estimated Revenue ($)" k="estimated_revenue" type="number" />
            <LeadField form={form} set={set} label="Projected # Schools" k="projected_schools" type="number" />
          </div>

          {valuation && (
            <div className="rounded-xl border border-[#bfe3ee] bg-[#e6f4f8]/60 p-3" data-testid="pipeline-valuation">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-[#00698a]">Estimated Annual Value</div>
                  <div className="text-2xl font-bold text-slate-900" data-testid="pipeline-value-amount">{money(valuation.annual_total)}</div>
                </div>
                <button type="button" onClick={() => setShowCalc((s) => !s)} data-testid="how-calculated-toggle"
                  className="text-[12px] font-semibold text-[#00698a] underline underline-offset-2">How calculated</button>
              </div>
              {showCalc && (
                <div className="mt-2 border-t border-[#bfe3ee] pt-2 text-[12px] text-slate-700 space-y-1" data-testid="how-calculated-detail">
                  {(valuation.explanation?.tier_lines || []).map((t, i) => (
                    <div key={i} className="flex justify-between"><span>{t.label} · {t.detail}</span><span className="font-medium">{money(t.amount)}</span></div>
                  ))}
                  {valuation.portfolio_license_fee > 0 && (
                    <div className="flex justify-between"><span>{valuation.portfolio_license_display_name}</span><span className="font-medium">{money(valuation.portfolio_license_fee)}</span></div>
                  )}
                  <div className="flex justify-between border-t border-[#bfe3ee] pt-1 font-bold text-slate-900"><span>Total</span><span>{money(valuation.annual_total)}</span></div>
                  <div className="text-[10px] text-slate-500">Pricing v{valuation.pricing_version} · auto-calculated from MySchoolRx pricing engine</div>
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-600">Lead Source</Label>
            <select value={form.lead_source} onChange={(e) => set("lead_source", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-200 text-sm px-2" data-testid="lead-source">
              {["Direct", "Referral", "Conference", "NACSA", "Website", "Existing Relationship", "Other"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 text-sm" rows={2} data-testid="lead-notes" />
          </div>

          {dups && dups.has_duplicates && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="duplicate-warning">
              <div className="flex items-center gap-2 text-amber-800 text-sm font-semibold"><AlertTriangle className="h-4 w-4" />This lead may already exist</div>
              <ul className="mt-2 space-y-1">
                {dups.matches.map((m, i) => (
                  <li key={i} className="text-[12px] text-amber-900">
                    <b>{m.source}</b>: {m.organization || m.contact || m.email} {m.status ? `· ${m.status}` : ""} {m.sales_contact ? `· owned by ${m.sales_contact}` : ""} {m.claim_expiration ? `· claim to ${m.claim_expiration}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {dups && !dups.has_duplicates && (
            <div className="flex items-center gap-2 text-emerald-700 text-[12px]" data-testid="no-duplicate"><CheckCircle2 className="h-4 w-4" />No duplicates found.</div>
          )}
        </div>
        <div className="p-5 border-t border-slate-100 flex flex-wrap justify-between gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={check} disabled={checking} data-testid="check-duplicate-btn">
            {checking ? "Checking…" : "Check for duplicates"}
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={() => save(dups?.has_duplicates)} disabled={saving} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="create-lead-submit">
              {saving ? "Saving…" : dups?.has_duplicates ? "Create Anyway" : "Create Lead"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Lead detail drawer ── */
const LeadDetail = ({ leadId, onBack }) => {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [ovVal, setOvVal] = useState("");
  const [ovReason, setOvReason] = useState("");
  const [showCalc, setShowCalc] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [cForm, setCForm] = useState(null);
  const load = useCallback(() => sget(`/api/sales/leads/${leadId}`).then(setData).catch(() => {}), [leadId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  const l = data.lead;

  const sendWelcome = async () => {
    setBusy("welcome");
    try {
      const r = await spost(`/api/sales/leads/${leadId}/welcome-email`);
      toast.success(r.delivered ? "Welcome email sent via Resend" : "Welcome email queued (verify a Resend domain to deliver externally)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(""); }
  };
  const copyLink = async () => {
    try {
      const r = await sget(`/api/sales/leads/${leadId}/link`);
      await navigator.clipboard.writeText(r.link);
      toast.success("Signup link copied");
    } catch { toast.error("Could not get link"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy("note");
    try { await spost(`/api/sales/leads/${leadId}/activity`, { activity_type: "Note", notes: note }); setNote(""); load(); toast.success("Note added"); }
    catch { toast.error("Could not add note"); } finally { setBusy(""); }
  };
  const patchLead = (body) => axios.patch(`${API}/api/sales/leads/${leadId}`, body, { withCredentials: true });
  const saveOverride = async () => {
    if (!ovVal || Number(ovVal) <= 0) { toast.error("Enter an override amount"); return; }
    if (!ovReason.trim()) { toast.error("A reason is required for an override"); return; }
    setBusy("ov");
    try { await patchLead({ pipeline_override_value: Number(ovVal), pipeline_override_reason: ovReason.trim() }); setOvVal(""); setOvReason(""); load(); toast.success("Override saved & audited"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Override failed"); } finally { setBusy(""); }
  };
  const clearOverride = async () => {
    setBusy("ov");
    try { await patchLead({ clear_pipeline_override: true }); load(); toast.success("Reverted to calculated value"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not clear"); } finally { setBusy(""); }
  };
  const openEditContact = () => {
    setCForm({
      contact_first_name: l.contact_first_name || "", contact_last_name: l.contact_last_name || "",
      contact_email: l.contact_email || "", contact_phone: l.contact_phone || "", contact_title: l.contact_title || "",
    });
    setEditContact(true);
  };
  const saveContact = async () => {
    if (!cForm.contact_email.trim()) { toast.error("Email is required"); return; }
    setBusy("contact");
    try {
      await patchLead(cForm);
      setEditContact(false); load();
      toast.success("Contact updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update contact"); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="lead-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="lead-detail-back"><ArrowLeft className="h-4 w-4" />Back to leads</button>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">{l.organization_name}</h1>
          <p className="text-sm text-slate-500">{[l.contact_first_name, l.contact_last_name].filter(Boolean).join(" ")} · {l.contact_email || "no email"}{l.contact_phone ? ` · ${formatPhoneDisplay(l.contact_phone)}` : ""}{l.state ? ` · ${l.state}` : ""}
            <button onClick={openEditContact} className="ml-2 text-[#00698a] hover:text-[#00607d] underline underline-offset-2 text-xs" data-testid="lead-edit-contact-btn">Edit contact</button>
          </p>
          {editContact && cForm && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl" data-testid="lead-edit-contact-form">
              <div><Label className="text-xs">First name</Label><Input value={cForm.contact_first_name} onChange={(e) => setCForm({ ...cForm, contact_first_name: e.target.value })} className="mt-1 h-9 text-sm" data-testid="edit-contact-first" /></div>
              <div><Label className="text-xs">Last name</Label><Input value={cForm.contact_last_name} onChange={(e) => setCForm({ ...cForm, contact_last_name: e.target.value })} className="mt-1 h-9 text-sm" data-testid="edit-contact-last" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={cForm.contact_email} onChange={(e) => setCForm({ ...cForm, contact_email: e.target.value })} className="mt-1 h-9 text-sm" data-testid="edit-contact-email" /></div>
              <div><Label className="text-xs">Title</Label><Input value={cForm.contact_title} onChange={(e) => setCForm({ ...cForm, contact_title: e.target.value })} className="mt-1 h-9 text-sm" data-testid="edit-contact-title" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Phone</Label><PhoneField value={cForm.contact_phone} onChange={(v) => setCForm({ ...cForm, contact_phone: v })} testid="edit-contact-phone" className="mt-1" /></div>
              <div className="sm:col-span-2 flex gap-2">
                <Button size="sm" onClick={saveContact} disabled={busy === "contact"} className="bg-slate-900 text-white" data-testid="edit-contact-save">{busy === "contact" ? "Saving…" : "Save contact"}</Button>
                <Button size="sm" variant="outline" onClick={() => setEditContact(false)}>Cancel</Button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Badge className={`text-xs ${STAGE_COLORS[stageLabel(l.stage)] || STAGE_FALLBACK}`}>{stageLabel(l.stage) === "Closed Lost" && <X className="h-3 w-3 mr-0.5" />}{stageLabel(l.stage)}</Badge>
            <Badge variant="outline" className="text-xs capitalize">{l.lead_type}</Badge>
            <span className="text-[12px] text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" />claim to {fmtDate(l.claim_expiration_date)}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-link-btn"><Copy className="h-3.5 w-3.5 mr-1" />Copy Link</Button>
          <Button size="sm" onClick={sendWelcome} disabled={busy === "welcome"} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="send-welcome-btn">
            <Mail className="h-3.5 w-3.5 mr-1" />{busy === "welcome" ? "Sending…" : "Send Welcome Email"}
          </Button>
        </div>
      </div>

      {/* Pipeline value — projected vs actual, with audited override + How calculated */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mt-6" data-testid="lead-pipeline-panel">
        <h2 className="font-semibold text-slate-800 text-sm mb-3">Pipeline Value</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Projected Schools</div><div className="text-lg font-bold text-slate-900" data-testid="ld-projected-schools">{l.projected_schools ?? "—"}</div></div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Projected Pipeline Value</div>
            <div className="text-lg font-bold text-blue-700" data-testid="ld-projected-value">{l.projected_pipeline_value != null ? money(l.projected_pipeline_value) : "—"}</div>
            {l.pipeline_value_overridden && <span className="inline-block mt-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5" data-testid="ld-override-badge">Authorized Override</span>}
          </div>
          <div><div className="text-[11px] uppercase tracking-wide text-slate-400">System Calculated</div><div className="text-lg font-semibold text-slate-600" data-testid="ld-system-value">{l.pipeline_system_calculated_value != null ? money(l.pipeline_system_calculated_value) : "—"}</div></div>
          <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Actual Contract Value</div><div className="text-lg font-bold text-emerald-700" data-testid="ld-actual-value">{l.actual_contract_value != null ? money(l.actual_contract_value) : "—"}</div></div>
        </div>
        {l.pricing_breakdown && (
          <div className="mt-3">
            <button type="button" onClick={() => setShowCalc((s) => !s)} className="text-[12px] font-semibold text-[#00698a] underline underline-offset-2" data-testid="ld-how-calculated">How calculated</button>
            {showCalc && (
              <div className="mt-2 text-[12px] text-slate-700 space-y-1 border-t border-slate-100 pt-2" data-testid="ld-how-calculated-detail">
                {(l.pricing_breakdown.tier_lines || []).map((t, i) => (<div key={i} className="flex justify-between"><span>{t.label} · {t.detail}</span><span className="font-medium">{money(t.amount)}</span></div>))}
                {l.pricing_breakdown.portfolio_license_fee > 0 && (<div className="flex justify-between"><span>{l.pricing_breakdown.portfolio_license_display_name}</span><span className="font-medium">{money(l.pricing_breakdown.portfolio_license_fee)}</span></div>)}
                <div className="text-[11px] text-slate-500">{l.pricing_breakdown.summary}</div>
              </div>
            )}
          </div>
        )}
        {l.pipeline_value_overridden ? (
          <div className="mt-3 text-[12px] text-slate-600 border-t border-slate-100 pt-3" data-testid="ld-override-info">
            Overridden to <b>{money(l.projected_pipeline_value)}</b> — "{l.pipeline_override_reason}" · by {l.pipeline_override_by_name} · {fmtDate(l.pipeline_override_at)}
            <Button size="sm" variant="outline" className="ml-3 h-7" disabled={busy === "ov"} onClick={clearOverride} data-testid="ld-clear-override">Revert to calculated</Button>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3" data-testid="ld-override-form">
            <div><div className="text-[11px] text-slate-500 mb-1">Override value ($)</div><Input type="number" value={ovVal} onChange={(e) => setOvVal(e.target.value)} className="h-9 w-32" data-testid="ld-override-value" /></div>
            <div className="flex-1 min-w-[160px]"><div className="text-[11px] text-slate-500 mb-1">Reason (required)</div><Input value={ovReason} onChange={(e) => setOvReason(e.target.value)} className="h-9" placeholder="e.g. Strategic portfolio agreement" data-testid="ld-override-reason" /></div>
            <Button size="sm" className="h-9 bg-slate-900 text-white" disabled={busy === "ov"} onClick={saveOverride} data-testid="ld-override-save">Save override</Button>
          </div>
        )}
      </div>

      {/* Attribution chain — read-only trace of salesperson persistence */}
      {data.attribution_chain && (() => {
        const ac = data.attribution_chain;
        return (
          <div className="rounded-xl border border-slate-200 bg-white p-4 mt-6" data-testid="lead-attribution-panel">
            <h2 className="font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2">Attribution Chain
              {ac.is_referral && <Badge variant="outline" className="text-[10px] text-violet-700 border-violet-200" data-testid="attr-referral-badge">Referral</Badge>}
            </h2>
            <div className="flex flex-wrap items-stretch gap-2 text-[13px]">
              <div className="flex-1 min-w-[150px] rounded-lg bg-slate-50 border border-slate-100 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Salesperson</div>
                <div className="font-semibold text-slate-900" data-testid="attr-salesperson">{ac.salesperson_label}</div>
                {ac.salesperson?.email && <div className="text-[11px] text-slate-500">{ac.salesperson.email}</div>}
              </div>
              <div className="flex items-center text-slate-300">→</div>
              <div className="flex-1 min-w-[150px] rounded-lg bg-slate-50 border border-slate-100 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Lead Source</div>
                <div className="font-medium text-slate-800" data-testid="attr-source">{ac.source || "—"}</div>
                {ac.is_referral && ac.referrer_name && <div className="text-[11px] text-slate-500">by {ac.referrer_name}{ac.referrer_org ? ` · ${ac.referrer_org}` : ""}</div>}
              </div>
              <div className="flex items-center text-slate-300">→</div>
              <div className="flex-1 min-w-[150px] rounded-lg bg-slate-50 border border-slate-100 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Converted Account</div>
                <div className="font-medium text-slate-800 capitalize" data-testid="attr-converted">{ac.converted?.name || (ac.converted?.type === "none" ? "Not yet converted" : ac.converted?.type)}</div>
                {ac.converted?.type !== "none" && <div className="text-[11px] text-slate-500 capitalize">{ac.converted.type}</div>}
              </div>
              <div className="flex items-center text-slate-300">→</div>
              <div className="flex-1 min-w-[150px] rounded-lg bg-slate-50 border border-slate-100 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">License / Renewal</div>
                {(!ac.licenses || ac.licenses.length === 0) ? (
                  <div className="text-slate-500" data-testid="attr-licenses-empty">None</div>
                ) : (
                  <div className="space-y-1" data-testid="attr-licenses">
                    {ac.licenses.slice(0, 4).map((lic, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <span className="text-slate-700 truncate">{lic.status || "—"}{lic.price != null ? ` · ${money(lic.price)}` : ""}</span>
                        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${lic.attributed ? "bg-emerald-500" : "bg-amber-400"}`} title={lic.attributed ? "Attributed to salesperson" : "Not attributed"} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-semibold text-slate-800 text-sm mb-3">Timeline</h2>
          <ul className="space-y-2" data-testid="lead-timeline">
            {(l.timeline || []).slice().reverse().map((e, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px]">
                <span className="h-2 w-2 rounded-full bg-[#007a9e] mt-1.5" />
                <div><span className="text-slate-700 font-medium capitalize">{e.event.replace(/_/g, " ")}</span>
                  <span className="text-slate-400 ml-2">{fmtDate(e.at)}</span></div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-semibold text-slate-800 text-sm mb-3">Email History</h2>
          {data.email_history.length === 0 ? <p className="text-[13px] text-slate-400">No emails yet.</p> : (
            <ul className="space-y-2" data-testid="lead-email-history">
              {data.email_history.map((e, i) => (
                <li key={i} className="text-[13px] flex items-center justify-between">
                  <span className="text-slate-700">{e.subject}</span>
                  <Badge variant="outline" className={`text-[10px] ${e.status === "sent" ? "text-emerald-600" : e.status === "failed" ? "text-red-600" : "text-slate-500"}`}>{e.status}</Badge>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4">
            <h3 className="font-semibold text-slate-800 text-sm mb-2">Add Note</h3>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="text-sm" placeholder="Log a call, meeting, or note…" data-testid="activity-note-input" />
            <Button size="sm" onClick={addNote} disabled={busy === "note"} className="mt-2" data-testid="add-note-btn">Add Note</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Leads list ── */
const Leads = ({ autoCreate, onAutoHandled }) => {
  const [leads, setLeads] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => sget(`/api/sales/leads${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(setLeads).catch(() => setLeads([])), [q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (autoCreate) { setShowCreate(true); onAutoHandled && onAutoHandled(); } }, [autoCreate, onAutoHandled]);

  const [pill, setPill] = useState("All");
  const [stageF, setStageF] = useState("");
  const [typeF, setTypeF] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [pill, stageF, typeF, q]);

  const list = leads || [];
  const OPEN = ["New", "Contacted", "Engaged", "Activating", "Account Created"];
  const inPill = (l, p) => {
    const lab = stageLabel(l.stage);
    if (p === "Open pipeline") return OPEN.includes(lab);
    if (p === "Closed won") return ["Closed Won", "Customer"].includes(lab);
    if (p === "Renewals") return lab === "Renewal/Closed";
    if (p === "Closed lost") return lab === "Closed Lost";
    return true;
  };
  const pillDefs = ["All", "Open pipeline", "Closed won", "Renewals", "Closed lost"];
  const counts = Object.fromEntries(pillDefs.map((p) => [p, list.filter((l) => inPill(l, p)).length]));
  const stageOpts = Array.from(new Set(list.map((l) => stageLabel(l.stage)))).sort();
  const typeOpts = Array.from(new Set(list.map((l) => l.lead_type).filter(Boolean))).sort();
  const filtered = list.filter((l) => inPill(l, pill) && (!stageF || stageLabel(l.stage) === stageF) && (!typeF || l.lead_type === typeF));
  const PER = 25;
  const pages = Math.max(1, Math.ceil(filtered.length / PER));
  const paged = filtered.slice((page - 1) * PER, page * PER);

  if (selected) return <LeadDetail leadId={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div data-testid="sales-leads">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-bold text-slate-900">Leads</h1><p className="text-sm text-slate-500 mt-1">Create, claim, and nurture opportunities.</p></div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="new-lead-btn"><Plus className="h-4 w-4 mr-1" />New Lead</Button>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organization, email, name…" className="max-w-xs h-9 text-sm" data-testid="lead-search" />
        <select value={stageF} onChange={(e) => setStageF(e.target.value)} className="h-9 rounded-md border border-slate-200 text-sm px-2 bg-white" data-testid="lead-stage-filter">
          <option value="">All stages</option>
          {stageOpts.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={typeF} onChange={(e) => setTypeF(e.target.value)} className="h-9 rounded-md border border-slate-200 text-sm px-2 bg-white capitalize" data-testid="lead-type-filter">
          <option value="">All types</option>
          {typeOpts.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="lead-quick-filters">
        {pillDefs.map((p) => (
          <button key={p} type="button" onClick={() => setPill(p)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${pill === p ? "bg-[#007a9e] text-white border-[#007a9e]" : "bg-white text-slate-600 border-slate-200 hover:border-[#007a9e]/40"}`}
            data-testid={`lead-pill-${p.toLowerCase().replace(/ /g, "-")}`}>
            {p}<span className={`rounded-full px-1.5 text-[10px] ${pill === p ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{counts[p]}</span>
          </button>
        ))}
      </div>
      <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
        {leads === null ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
          : leads.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No leads yet. Click "New Lead" to create your first.</div>
            : filtered.length === 0 ? <div className="p-10 text-center text-sm text-slate-400" data-testid="lead-no-match">No leads match these filters.</div>
            : (
              <>
              <table className="hidden sm:table w-full text-sm min-w-[640px]">
                <thead><tr className="text-left text-[11.5px] uppercase text-slate-500 bg-[#f9fafb] border-b border-slate-100">
                  <th className="p-3">Organization</th><th className="p-3">Contact</th><th className="p-3">Type</th><th className="p-3">Stage</th><th className="p-3">Expected Close</th><th className="p-3 text-right">Est. Revenue</th><th className="p-3">Claim Exp.</th>
                </tr></thead>
                <tbody>
                  {paged.map((l) => (
                    <tr key={l.id} onClick={() => setSelected(l.id)} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`lead-row-${l.id}`}>
                      <td className="p-3">
                        <div className="font-semibold text-[#00698a]">{l.organization_name}{l.is_test_data && <TestBadge />}</div>
                        {(l.state || l.projected_schools) && <div className="text-[12px] text-slate-400 mt-0.5">{[l.state, l.projected_schools ? `${l.projected_schools} school(s)` : null].filter(Boolean).join(" · ")}</div>}
                      </td>
                      <td className="p-3 text-slate-600">
                        <div>{[l.contact_first_name, l.contact_last_name].filter(Boolean).join(" ") || "—"}</div>
                        {l.contact_title && <div className="text-[12px] text-slate-400">{l.contact_title}</div>}
                      </td>
                      <td className="p-3 capitalize text-slate-600">{l.lead_type}</td>
                      <td className="p-3"><StageChip stage={l.stage} /></td>
                      <td className="p-3 text-slate-500" data-testid="lead-expected-close">{fmtDate(l.expected_close_date)}</td>
                      <td className="p-3 text-right text-slate-600 tabular-nums">{l.estimated_revenue ? money(l.estimated_revenue) : "—"}</td>
                      <td className="p-3 text-slate-500">{claimExp(l.claim_expiration_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="sm:hidden divide-y divide-slate-100">
                {paged.map((l) => (
                  <div key={l.id} onClick={() => setSelected(l.id)} className="p-4 cursor-pointer" data-testid={`lead-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-[#00698a]">{l.organization_name}{l.is_test_data && <TestBadge />}</div>
                      <StageChip stage={l.stage} className="shrink-0" />
                    </div>
                    <div className="text-[13px] text-slate-500 mt-0.5">{[l.contact_first_name, l.contact_last_name].filter(Boolean).join(" ") || "—"} · <span className="capitalize">{l.lead_type}</span></div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-500">
                      <span data-testid="lead-expected-close">Close: {fmtDate(l.expected_close_date)}</span>
                      <span>Est: {l.estimated_revenue ? money(l.estimated_revenue) : "—"}</span>
                      <span>Claim: {fmtDate(l.claim_expiration_date)}</span>
                    </div>
                  </div>
                ))}
              </div>
              {pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-[12px] text-slate-500" data-testid="lead-pagination">
                  <span>{(page - 1) * PER + 1}–{Math.min(page * PER, filtered.length)} of {filtered.length}</span>
                  <div className="flex items-center gap-2">
                    <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40" data-testid="lead-page-prev">Prev</button>
                    <span>{page} / {pages}</span>
                    <button type="button" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40" data-testid="lead-page-next">Next</button>
                  </div>
                </div>
              )}
              </>
            )}
      </div>
      {showCreate && <CreateLeadModal onClose={() => setShowCreate(false)} onCreated={(l) => { setShowCreate(false); load(); setSelected(l.id); }} />}
    </div>
  );
};

/* ── Account views (attributed-only) ── */
const SimpleTable = ({ endpoint, columns, testid, empty }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { sget(endpoint).then(setRows).catch(() => setRows([])); }, [endpoint]);
  return (
    <div className="rounded-xl border border-slate-200 bg-white sm:overflow-x-auto" data-testid={testid}>
      {rows === null ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
        : rows.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">{empty}</div>
          : (
            <>
              {/* Desktop table */}
              <table className="hidden sm:table w-full text-sm min-w-[640px]">
                <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-100">
                  {columns.map((c) => <th key={c.k} className="p-3">{c.label}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-b border-slate-50 hover:bg-slate-50 ${r.is_test_data ? "bg-amber-50/40" : ""}`} data-testid={r.is_test_data ? "test-data-row" : undefined}>
                      {columns.map((c, ci) => (
                        <td key={c.k} className="p-3 text-slate-700">
                          {c.render ? c.render(r) : (r[c.k] ?? "—")}
                          {ci === 0 && r.is_test_data && <TestBadge />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <div key={i} className={`p-4 ${r.is_test_data ? "bg-amber-50/40" : ""}`} data-testid={r.is_test_data ? "test-data-row" : undefined}>
                    <div className="font-semibold text-slate-800 mb-2">
                      {columns[0].render ? columns[0].render(r) : (r[columns[0].k] ?? "—")}
                      {r.is_test_data && <TestBadge />}
                    </div>
                    <dl className="space-y-1.5">
                      {columns.slice(1).map((c) => (
                        <div key={c.k} className="flex items-start justify-between gap-3 text-[13px]">
                          <dt className="text-slate-400">{c.label}</dt>
                          <dd className="text-right text-slate-700">{c.render ? c.render(r) : (r[c.k] ?? "—")}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            </>
          )}
    </div>
  );
};

const MyPortfolio = () => (
  <div data-testid="sales-portfolios">
    <h1 className="text-2xl font-bold text-slate-900">My Portfolio</h1>
    <p className="text-sm text-slate-500 mt-1 mb-5">Portfolio accounts attributed to you.</p>
    <SimpleTable endpoint="/api/sales/portfolios" testid="my-portfolio-table" empty="No portfolios yet — create a portfolio lead and convert it."
      columns={[
        { k: "organization_name", label: "Organization" },
        { k: "manager_name", label: "Manager" },
        { k: "state", label: "State" },
        { k: "stage", label: "Stage", render: (r) => <StageChip stage={r.stage} /> },
        { k: "portfolio_status", label: "Status" },
        { k: "n_schools", label: "# Schools" },
        { k: "claim_expiration_date", label: "Claim Exp.", render: (r) => fmtDate(r.claim_expiration_date) },
      ]} />
  </div>
);
const MySchools = () => (
  <div data-testid="sales-schools">
    <h1 className="text-2xl font-bold text-slate-900">My Schools</h1>
    <p className="text-sm text-slate-500 mt-1 mb-5">Schools across your sales network.</p>
    <SimpleTable endpoint="/api/sales/schools" testid="my-schools-table" empty="No schools yet."
      columns={[
        { k: "school_name", label: "School" },
        { k: "city", label: "City" }, { k: "state", label: "State" },
        { k: "activation_status", label: "Activation" },
        { k: "diagnostic_status", label: "Diagnostic" },
        { k: "last_activity", label: "Last Activity", render: (r) => fmtDate(r.last_activity) },
      ]} />
  </div>
);
const MyUsers = () => (
  <div data-testid="sales-users">
    <h1 className="text-2xl font-bold text-slate-900">My Users</h1>
    <p className="text-sm text-slate-500 mt-1 mb-5">Users across your network.</p>
    <SimpleTable endpoint="/api/sales/users" testid="my-users-table" empty="No users yet."
      columns={[
        { k: "name", label: "Name" }, { k: "email", label: "Email" },
        { k: "role", label: "Role" }, { k: "account_type", label: "Account" },
        { k: "status", label: "Status" },
        { k: "last_login", label: "Last Login", render: (r) => fmtDate(r.last_login) },
      ]} />
  </div>
);
const MyLicenses = () => (
  <div data-testid="sales-licenses">
    <h1 className="text-2xl font-bold text-slate-900">My Licenses</h1>
    <p className="text-sm text-slate-500 mt-1 mb-5">License & renewal status for your accounts.</p>
    <SimpleTable endpoint="/api/sales/licenses" testid="my-licenses-table" empty="No licenses yet."
      columns={[
        { k: "account_name", label: "Account" }, { k: "account_type", label: "Type" },
        { k: "license_type", label: "License" },
        { k: "license_status", label: "Status", render: (r) => <Badge variant="outline" className="text-[11px]">{r.license_status}</Badge> },
        { k: "annual_amount", label: "Annual", render: (r) => (r.annual_amount != null ? "$" + Number(r.annual_amount).toLocaleString() : "—") },
        { k: "sales_contact", label: "Sales Contact", render: (r) => r.sales_contact || "—" },
        { k: "renewal_date", label: "Renewal", render: (r) => fmtDate(r.renewal_date || r.end_date) },
      ]} />
  </div>
);
const Activity = () => (
  <div data-testid="sales-activity">
    <h1 className="text-2xl font-bold text-slate-900">Activity</h1>
    <p className="text-sm text-slate-500 mt-1 mb-5">Your logged calls, notes, and follow-ups.</p>
    <SimpleTable endpoint="/api/sales/activities" testid="activity-table" empty="No activity yet — add notes from a lead."
      columns={[
        { k: "activity_date", label: "Date", render: (r) => fmtDate(r.activity_date) },
        { k: "activity_type", label: "Type" },
        { k: "lead_name", label: "Lead" },
        { k: "notes", label: "Notes" },
        { k: "next_follow_up_date", label: "Follow-up", render: (r) => fmtDate(r.next_follow_up_date) },
      ]} />
  </div>
);

/* ── Test Lab ── */
const TestLab = () => {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState("");
  const [custom, setCustom] = useState({ portfolios: 1, schools_per_portfolio: 2, single_school_leads: 1, prospects: 1, include_diagnostics: true, stage: "Account Created" });
  const [sim, setSim] = useState({ prospects: [], portfolios: [], schools: [] });
  const [accounts, setAccounts] = useState([]);
  const load = useCallback(() => {
    sget("/api/sales/test-lab/status").then(setStatus).catch(() => setStatus({ enabled: false, templates: [] }));
    sget("/api/sales/test-lab/runs").then(setRuns).catch(() => setRuns([]));
    Promise.all([
      sget("/api/sales/leads").catch(() => []),
      sget("/api/sales/portfolios").catch(() => []),
      sget("/api/sales/schools").catch(() => []),
    ]).then(([leads, pfs, schools]) => setSim({
      prospects: leads.filter((l) => l.is_test_data && !l.converted_user_id),
      portfolios: pfs.filter((p) => p.is_test_data && p.portfolio_id),
      schools: schools.filter((s) => s.is_test_data && s.tenant_id),
    }));
    sget("/api/sales/test-lab/impersonatable").then(setAccounts).catch(() => setAccounts([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); toast.success(typeof okMsg === "function" ? okMsg(r) : okMsg); load(); return r; }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };
  const generate = (template) => run(template, () => spost("/api/sales/test-lab/generate", { template }), (r) => `Generated ${r.total} test records (${r.template_label})`);
  const generateCustom = () => run("custom", () => spost("/api/sales/test-lab/custom-scenario", custom), (r) => `Custom scenario: ${r.total} records`);
  const delRun = (id) => run(id, () => axios.delete(`${API}/api/sales/test-lab/runs/${id}`, cfg).then((r) => r.data), "Test run deleted");
  const purgeAll = () => run("purge", () => spost("/api/sales/test-lab/purge"), (r) => `Deleted ${r.runs} test run(s)`);
  const simSignup = (id) => run(id, () => spost(`/api/sales/test-lab/leads/${id}/simulate-signup`, { send_email: false }), "Signup simulated — account created");
  const simSetup = (id) => run(id, () => spost(`/api/sales/test-lab/portfolios/${id}/simulate-setup`, { schools: 2 }), "Added 2 test schools");
  const simActivate = (id) => run(id, () => spost(`/api/sales/test-lab/schools/${id}/simulate-activation`), "School activated + diagnostic approved");
  const openAs = async (u) => {
    setBusy(u.user_id);
    try {
      const r = await spost("/api/sales/test-lab/impersonate", { user_id: u.user_id });
      let url;
      if (r.mode === "diag") {
        try { localStorage.setItem("diag_session", JSON.stringify(r.diag_session)); } catch {}
        url = `/test-lab/impersonate?mode=diag&redirect=${encodeURIComponent(r.redirect)}&email=${encodeURIComponent(r.email)}`;
      } else {
        url = `/test-lab/impersonate?mode=session&token=${encodeURIComponent(r.token)}&redirect=${encodeURIComponent(r.redirect)}&email=${encodeURIComponent(r.email)}`;
      }
      window.open(url, "_blank");
      toast.success(`Opened test session as ${u.kind} (${r.expires_minutes}m)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open test session"); }
    finally { setBusy(""); }
  };
  const setC = (k, v) => setCustom((p) => ({ ...p, [k]: v }));

  if (!status) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;

  const numField = (k, label, max = 15) => (
    <div>
      <Label className="text-[11px] text-slate-500">{label}</Label>
      <Input type="number" min={0} max={max} value={custom[k]} onChange={(e) => setC(k, Math.max(0, parseInt(e.target.value || "0", 10)))} className="mt-1 h-9 text-sm" data-testid={`custom-${k}`} />
    </div>
  );

  return (
    <div data-testid="sales-test-lab">
      <div className="flex items-center gap-2">
        <FlaskConical className="h-6 w-6 text-amber-500" />
        <h1 className="text-2xl font-bold text-slate-900">Test Lab</h1>
      </div>
      <p className="text-sm text-slate-500 mt-1">
        Generate realistic, clearly-tagged test data to exercise the full sales workflow. Every record is badged
        <TestBadge /> and can be deleted at any time. Emails are simulated only — nothing is sent externally.
      </p>

      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        Test records appear in your normal Leads, Portfolios, Schools, Users, and Licenses views with a "Test Data" badge. Delete them here when you're done.
      </div>

      <h2 className="mt-8 font-semibold text-slate-800">Quick Generators</h2>
      <div className="grid sm:grid-cols-3 gap-4 mt-3">
        {(status.templates || []).map((t) => (
          <div key={t.key} className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col" data-testid={`test-template-${t.key}`}>
            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /><h2 className="font-semibold text-slate-800">{t.label}</h2></div>
            <p className="text-[13px] text-slate-500 mt-1 flex-1">{t.desc}</p>
            <Button onClick={() => generate(t.key)} disabled={!!busy} className="mt-4 bg-amber-500 hover:bg-amber-600 text-white" data-testid={`generate-${t.key}-btn`}>
              {busy === t.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Generate {t.label}</>}
            </Button>
          </div>
        ))}
      </div>

      {/* Custom Scenario */}
      <h2 className="mt-8 font-semibold text-slate-800 flex items-center gap-2"><Settings2 className="h-4 w-4 text-slate-500" />Custom Scenario</h2>
      <div className="mt-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="custom-scenario">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {numField("portfolios", "# Portfolios", 10)}
          {numField("schools_per_portfolio", "Schools / portfolio", 15)}
          {numField("single_school_leads", "Single-school leads", 20)}
          {numField("prospects", "Prospect leads", 20)}
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-[11px] text-slate-500">Converted stage</Label>
            <select value={custom.stage} onChange={(e) => setC("stage", e.target.value)} className="mt-1 h-9 rounded-md border border-slate-200 text-sm px-2 block" data-testid="custom-stage">
              {["New", "Contacted", "Engaged", "Account Created", "Activating", "Active Customer", "Renewal/Closed"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 pb-2" data-testid="custom-diagnostics">
            <input type="checkbox" checked={custom.include_diagnostics} onChange={(e) => setC("include_diagnostics", e.target.checked)} />
            Include diagnostics
          </label>
          <Button onClick={generateCustom} disabled={!!busy} className="ml-auto bg-amber-500 hover:bg-amber-600 text-white" data-testid="generate-custom-btn">
            {busy === "custom" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Scenario"}
          </Button>
        </div>
      </div>

      {/* Simulation actions */}
      <h2 className="mt-8 font-semibold text-slate-800 flex items-center gap-2"><Play className="h-4 w-4 text-slate-500" />Simulate Workflow Steps</h2>
      <div className="mt-3 grid md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sim-signups">
          <div className="text-sm font-semibold text-slate-700 mb-2">Simulate Signup <span className="text-[11px] font-normal text-slate-400">({sim.prospects.length} prospects)</span></div>
          {sim.prospects.length === 0 ? <p className="text-[12px] text-slate-400">No unconverted test leads.</p> : sim.prospects.slice(0, 8).map((l) => (
            <div key={l.id} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
              <span className="text-[13px] text-slate-700 truncate mr-2">{l.organization_name}</span>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => simSignup(l.id)} disabled={!!busy} data-testid={`sim-signup-${l.id}`}>Sign up</Button>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sim-setups">
          <div className="text-sm font-semibold text-slate-700 mb-2">Portfolio Setup <span className="text-[11px] font-normal text-slate-400">({sim.portfolios.length} portfolios)</span></div>
          {sim.portfolios.length === 0 ? <p className="text-[12px] text-slate-400">No test portfolios.</p> : sim.portfolios.slice(0, 8).map((p) => (
            <div key={p.portfolio_id} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
              <span className="text-[13px] text-slate-700 truncate mr-2">{p.organization_name}</span>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => simSetup(p.portfolio_id)} disabled={!!busy} data-testid={`sim-setup-${p.portfolio_id}`}>+2 schools</Button>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sim-activations">
          <div className="text-sm font-semibold text-slate-700 mb-2">School Activation <span className="text-[11px] font-normal text-slate-400">({sim.schools.length} schools)</span></div>
          {sim.schools.length === 0 ? <p className="text-[12px] text-slate-400">No test schools.</p> : sim.schools.slice(0, 8).map((s) => (
            <div key={s.tenant_id} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
              <span className="text-[13px] text-slate-700 truncate mr-2">{s.school_name}</span>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => simActivate(s.tenant_id)} disabled={!!busy} data-testid={`sim-activate-${s.tenant_id}`}>Activate</Button>
            </div>
          ))}
        </div>
      </div>

      {/* Impersonation */}
      <h2 className="mt-8 font-semibold text-slate-800 flex items-center gap-2"><UserCog className="h-4 w-4 text-slate-500" />Open as Test User</h2>
      <p className="text-[12px] text-slate-500 mt-1">Launch a fully-interactive, 30-minute test session in a new tab. Restricted to test accounts.</p>
      <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="impersonation-table">
        {accounts.length === 0 ? <div className="p-8 text-center text-sm text-slate-400">No test accounts yet — generate a portfolio or school first.</div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-100">
              <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Type</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {accounts.map((u) => (
                <tr key={u.user_id} className="border-b border-slate-50" data-testid={`impersonate-row-${u.user_id}`}>
                  <td className="p-3 font-medium text-slate-800">{u.name}</td>
                  <td className="p-3 text-slate-600">{u.email}</td>
                  <td className="p-3"><Badge variant="outline" className="text-[11px]">{u.kind}</Badge></td>
                  <td className="p-3 text-right">
                    <Button size="sm" onClick={() => openAs(u)} disabled={!!busy} className="h-7 text-[11px] bg-slate-800 hover:bg-slate-900 text-white" data-testid={`open-as-${u.user_id}`}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />Open as {u.kind}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800">Test Runs</h2>
        {runs.length > 0 && (
          <Button size="sm" variant="outline" onClick={purgeAll} disabled={!!busy} className="text-red-600" data-testid="purge-all-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1" />Delete All Test Data
          </Button>
        )}
      </div>
      <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
        {runs.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No test runs yet. Generate one above.</div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-100">
              <th className="p-3">Template</th><th className="p-3">Records</th><th className="p-3">Status</th><th className="p-3">Created</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-slate-50" data-testid={`test-run-${r.id}`}>
                  <td className="p-3 font-medium text-slate-800">{r.template_label}</td>
                  <td className="p-3 text-slate-600">{r.total}</td>
                  <td className="p-3"><Badge variant="outline" className={`text-[11px] ${r.status === "complete" ? "text-emerald-600" : r.status === "failed" ? "text-red-600" : "text-slate-500"}`}>{r.status}</Badge></td>
                  <td className="p-3 text-slate-500">{fmtDate(r.created_at)}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => delRun(r.id)} disabled={!!busy} className="text-red-600 h-7" data-testid={`delete-run-${r.id}`}>
                      {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};


const NAV = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["leads", "Leads", Users],
  ["commission", "Commission", DollarSign],
  ["referrals", "Referrals", Share2],
  ["portfolio", "My Portfolio", Building2],
  ["schools", "My Schools", School],
  ["users", "My Users", Users],
  ["licenses", "My Licenses", Target],
  ["activity", "Activity", Clock],
];

const refHeaders = (me) => ({
  "x-user-id": me?.id || me?.user_id || "public",
  "x-user-role": me?.role || "sales",
  "x-tenant-id": me?.tenant_id || "tenant_1",
});

function Referrals({ me }) {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const h = { headers: refHeaders(me), withCredentials: true };
    axios.get(`${API}/api/admin/referrals/stats`, h).then((r) => setStats(r.data)).catch(() => setStats(null));
    axios.get(`${API}/api/admin/referrals`, h).then((r) => setRows(r.data.referrals || [])).catch(() => setRows([]));
  }, [me]);

  const S = stats?.by_status || {};
  const tiles = [
    { label: "Total Referrals", val: stats?.total ?? "—" },
    { label: "Registered", val: S["Registered"] ?? "—" },
    { label: "Pilot Active", val: S["Pilot Active"] ?? "—" },
    { label: "Converted", val: stats?.converted ?? "—" },
    { label: "Conversion %", val: stats ? `${stats.conversion_pct}%` : "—" },
  ];
  const STATUS = {
    "Referral Sent": "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
    Visited: "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
    Registered: "bg-[#f1f5f9] text-[#334155] border border-[#cbd5e1]",
    "Pilot Active": "bg-[#e6f4f8] text-[#00698a] border border-[#bfe3ee]",
    Customer: "bg-[#00698a] text-white",
  };

  return (
    <div className="space-y-5" data-testid="sales-referrals">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Share2 className="h-5 w-5 text-[#007a9e]" /> Referral Leads</h2>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-2xl font-bold text-slate-900">{t.val}</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-0.5">{t.label}</div>
          </div>
        ))}
      </div>
      {stats && (stats.top_orgs?.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-semibold text-slate-500 mb-2">Top Referring Organizations</div>
            {stats.top_orgs.map((o) => (
              <div key={o.name} className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0"><span className="truncate text-slate-700">{o.name}</span><span className="font-medium">{o.count}</span></div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-semibold text-slate-500 mb-2">Top Referring Users</div>
            {stats.top_users.map((u) => (
              <div key={u.name} className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0"><span className="truncate text-slate-700">{u.name}</span><span className="font-medium">{u.count}</span></div>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
            <th className="text-left px-3 py-2">Recipient</th><th className="text-left px-3 py-2">Referred By</th>
            <th className="text-left px-3 py-2">Sales</th><th className="text-left px-3 py-2">Status</th>
          </tr></thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">No referrals yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`referral-row-${r.id}`}>
                <td className="px-3 py-2"><div className="font-medium text-slate-800">{r.recipient_org || r.recipient_name}</div><div className="text-[11px] text-slate-400">{r.recipient_email}</div></td>
                <td className="px-3 py-2 text-slate-600">{r.referrer_org || r.referrer_name || "—"}</td>
                <td className="px-3 py-2 text-slate-600">{r.sales_employee_name || "Direct"}</td>
                <td className="px-3 py-2"><Badge className={`text-[11px] ${STATUS[r.status] || "bg-slate-100"}`}>{r.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SalesPortalArea() {
  const navigate = useNavigate();
  const [me, setMe] = useState(undefined);
  const [tab, setTab] = useState("dashboard");
  const [createOnLeads, setCreateOnLeads] = useState(false);
  const [testLabEnabled, setTestLabEnabled] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, cfg)
      .then((r) => {
        const role = (r.data?.role || "").toLowerCase();
        if (r.data?.must_change_password || r.data?.password_reset_required) {
          navigate("/change-password", { replace: true }); return;
        }
        if (!["sales", "admin", "super_admin"].includes(role)) { navigate("/app", { replace: true }); return; }
        setMe(r.data);
        sget("/api/sales/test-lab/status").then((s) => setTestLabEnabled(!!s.enabled)).catch(() => {});
      })
      .catch(() => { setMe(null); navigate("/app", { replace: true }); });
  }, [navigate]);

  if (me === undefined) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (!me) return null;

  const nav = testLabEnabled ? [...NAV, ["testlab", "Test Lab", FlaskConical]] : NAV;
  const logout = () => axios.post(`${API}/api/auth/logout`, {}, cfg).finally(() => { try { localStorage.clear(); sessionStorage.clear(); } catch {} navigate("/diagnostic/login", { replace: true }); });
  let isDemoEnv = false, demoEmail = "";
  try { isDemoEnv = sessionStorage.getItem("impersonation_mode") === "demo"; demoEmail = sessionStorage.getItem("impersonation_email") || ""; } catch { /* ignore */ }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex" data-testid="sales-portal">
      <aside aria-label="Workspace" className="w-60 shrink-0 bg-white border-r border-[#e5e7eb] min-h-screen p-4 hidden md:block">
        <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="w-[156px] h-auto max-w-full object-contain" />
        <div className="mt-1 text-[11.5px] font-bold uppercase tracking-wider text-[#00698a]">Sales Portal</div>
        <nav aria-label="Main" className="mt-6 space-y-1">
          {nav.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`sales-nav-${k}`}
              aria-current={tab === k ? "page" : undefined}
              className={`w-full flex items-center gap-2 px-3 h-11 rounded-lg text-[15px] font-semibold ${tab === k ? "bg-[#e6f4f8] text-[#00698a] relative before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded before:bg-[#007a9e]" : "text-[#374151] hover:bg-[#f9fafb]"}`}>
              <Icon className="h-[18px] w-[18px]" />{label}
            </button>
          ))}
        </nav>
      </aside>
      <main id="main" className="flex-1 min-w-0 flex flex-col">
        <PortalTopBar
          label="Sales"
          orgName="589 Labs"
          accent="#334e68"
          onSignOut={logout}
          feedbackPortalType="Sales"
        />
        {isDemoEnv && (
          <div role="note" data-testid="sales-demo-strip" className="flex items-center justify-between gap-2 bg-[#e6f4f8] px-6 py-2 text-[13.5px] font-semibold text-[#00698a]">
            <span>Demo environment — isolated demo data</span>
            <span className="hidden sm:inline text-[#1f2937] font-normal">{demoEmail}</span>
          </div>
        )}
        <div className="p-5 sm:p-8 w-full max-w-6xl">
        {/* Mobile nav tabs */}
        <div className="md:hidden flex gap-2 mb-4 overflow-x-auto pb-1">
          {nav.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`sales-nav-m-${k}`} aria-current={tab === k ? "page" : undefined} className={`flex items-center gap-1 px-3 h-9 rounded-lg text-xs font-semibold shrink-0 ${tab === k ? "bg-[#e6f4f8] text-[#00698a]" : "bg-white text-[#374151]"}`}><Icon className="h-4 w-4" />{label}</button>
          ))}
        </div>
        {tab === "dashboard" && <Dashboard me={me} onGoLeads={() => setTab("leads")} onNewLead={() => { setTab("leads"); setCreateOnLeads(true); }} onGoCommission={() => setTab("commission")} />}
        {tab === "leads" && <Leads autoCreate={createOnLeads} onAutoHandled={() => setCreateOnLeads(false)} />}
        {tab === "commission" && <Commission />}
        {tab === "referrals" && <Referrals me={me} />}
        {tab === "portfolio" && <MyPortfolio />}
        {tab === "schools" && <MySchools />}
        {tab === "users" && <MyUsers />}
        {tab === "licenses" && <MyLicenses />}
        {tab === "activity" && <Activity />}
        {tab === "testlab" && <TestLab />}
        </div>
      </main>
    </div>
  );
}
